// Minhas faturas (PO1-3160): a transportadora vê as próprias faturas em cada
// embarcador com vínculo ativo, com a situação traduzida (em auditoria,
// reprovada com motivo, contestada, liberada, integrada, cancelada) e o filtro
// de vencidas / a vencer do portal antigo. A API devolve só os campos seguros.
// PO1-3222: fatura reprovada mostra os CT-e em questão e permite Contestar ou
// Cancelar (AcoesReprovada).
// PO1-3646 (épico PO1-3359): é a home da transportadora; quando o único
// embarcador é a Rede Everlog (soRede), mostra o boas-vindas com os próximos
// passos. Faturas/Extrato/SFTP continuam no menu (decisão do kickoff).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import MetricCardGroup from "components/MetricCardGroup";
import MetricCard from "components/MetricCard";
import ShellCard, { TableSurface } from "components/ShellCard";
import SectionCard from "components/SectionCard";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import { listarFaturas, resumoFaturas, redeStatus, mensagemErro } from "services/meuFreteApi";
import { reactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import { fmtBRL, fmtData } from "views/Empresa/ConferenciaFatura/formatters";
import { StatusBadge, getVencimentoAlert } from "views/Empresa/ConferenciaFatura/componentesCompartilhados";

import AcoesReprovada from "./AcoesReprovada";
import {
  ROTULOS,
  DATA_CAMPOS,
  SITUACOES_ALERTA_VENCIMENTO,
  FILTROS_VAZIOS,
  filtrosDaUrl,
  montarParams,
  toneDe,
} from "./rotulos";

// PO1-3171: o `tone` do MetricCard só pinta a caixa do ícone — sem
// `iconClassName` a cor nunca aparecia. Ícones das tim-icons já usados em
// outros cards do Hub; os tons vêm de rotulos.js (danger = alias no MetricCard).
const ICONE_SITUACAO = {
  em_auditoria: "tim-icons icon-bullet-list-67",
  reprovada: "tim-icons icon-alert-circle-exc",
  contestada: "tim-icons icon-time-alarm",
  liberada: "tim-icons icon-check-2",
  integrada: "tim-icons icon-money-coins",
  cancelada: "tim-icons icon-simple-remove",
};

const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_SORT = { field: "emissao", dir: "desc" };
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const filterLabelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };

function toggleSort(sort, field) {
  if (sort.field === field) return { field, dir: sort.dir === "asc" ? "desc" : "asc" };
  return { field, dir: "asc" };
}

function LinhaResumo({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.35rem 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
      <span style={mutedStyle}>{label}</span>
      <span style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.86rem", textAlign: "right" }}>{value || "—"}</span>
    </div>
  );
}

export default function MinhasFaturas() {
  const location = useLocation();
  const { addToast } = useToast();
  const { empresa, isAllEmpresas } = useCompanies();
  const embarcadorId = empresa && !isAllEmpresas ? empresa.value : null;

  const [filtros, setFiltros] = useState(() => ({ ...FILTROS_VAZIOS, ...filtrosDaUrl(location.search) }));
  const [aplicados, setAplicados] = useState(() => ({ ...FILTROS_VAZIOS, ...filtrosDaUrl(location.search) }));
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dados, setDados] = useState({ items: [], total: 0 });
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [selecionada, setSelecionada] = useState(null);
  // PO1-3646: boas-vindas da Rede Everlog quando ela é o único embarcador.
  const [soRede, setSoRede] = useState(false);

  useEffect(() => {
    let ativo = true;
    redeStatus()
      .then((r) => {
        if (ativo) setSoRede(Boolean(r && r.soRede));
      })
      .catch(() => {
        if (ativo) setSoRede(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setLoading(true);
    setErro(null);
    try {
      const params = montarParams({ filtros: aplicados, embarcadorId, page, pageSize, sort });
      const [lista, res] = await Promise.all([listarFaturas(params), resumoFaturas(params)]);
      setDados({ items: lista.items || [], total: Number(lista.total) || 0 });
      setResumo(res);
    } catch (err) {
      const msg = mensagemErro(err, "Não foi possível carregar as faturas.");
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Erro ao carregar as faturas", description: msg });
    } finally {
      setLoading(false);
    }
  }, [empresa, embarcadorId, aplicados, page, pageSize, sort, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setSelecionada(null);
  }, [dados]);

  const aplicar = (parcial) => {
    const proximos = { ...filtros, ...(parcial || {}) };
    setFiltros(proximos);
    setAplicados(proximos);
    setPage(1);
  };

  const limpar = () => {
    setFiltros({ ...FILTROS_VAZIOS });
    setAplicados({ ...FILTROS_VAZIOS });
    setPage(1);
  };

  const activeFiltersCount = useMemo(
    () => ["situacao", "vencimento", "dataDe", "dataAte", "numero"].filter((k) => aplicados[k]).length,
    [aplicados]
  );

  const onSort = (field) => {
    setSort((atual) => toggleSort(atual, field));
    setPage(1);
  };

  const contexto = empresa ? empresa.label : "";

  return (
    <div className="content">
      <PageHero
        eyebrow="MeuFrete"
        title="Minhas faturas"
        description={`Situação das suas faturas na auditoria de cada embarcador. Contexto: ${contexto || "escolha o embarcador no topo"}.`}
        actions={<PageHeroRefreshButton onClick={carregar} loading={loading} />}
      />

      {soRede && (
        <SpecificEmpresaRequiredNotice
          variant="info"
          title="Bem-vindo à Rede Everlog"
          message="Complete seu cadastro, revise a cobertura, cadastre o contato de Cotação e, se quiser, envie sua tabela. É assim que os embarcadores encontram a sua transportadora no Sourcing, no Leilão e no BID."
        >
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.6rem" }}>
            <Link className="btn btn-sm btn-info" to="/app/meufrete/cadastro?aba=cobertura">Revisar a cobertura</Link>
          </div>
        </SpecificEmpresaRequiredNotice>
      )}

      {resumo && (
        <MetricCardGroup eyebrow="Resumo" title="Faturas por situação" description="Os cards também funcionam como filtro rápido.">
          {ROTULOS.map((r) => (
            <MetricCard
              key={r.codigo}
              label={r.rotulo}
              value={resumo.porRotulo ? resumo.porRotulo[r.codigo] || 0 : 0}
              iconClassName={ICONE_SITUACAO[r.codigo]}
              tone={r.tone}
              active={aplicados.situacao === r.codigo}
              onClick={() => aplicar({ situacao: aplicados.situacao === r.codigo ? "" : r.codigo })}
            />
          ))}
        </MetricCardGroup>
      )}

      <FilterBand
        mode="server"
        searchLabel="Número da fatura"
        searchHint="Busca por prefixo"
        searchPlaceholder="Ex.: 66530"
        searchValue={filtros.numero}
        onSearchChange={(v) => setFiltros((f) => ({ ...f, numero: v }))}
        onSearchSubmit={(v) => aplicar({ numero: v })}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Situação e período por emissão ou vencimento. Vencidas e a vencer ficam nos atalhos logo abaixo."
        onApply={() => aplicar()}
        onClear={limpar}
        busy={loading}
      >
        <Row>
          <Col lg="3" md="6">
            <Label style={filterLabelStyle}>Situação</Label>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={[{ value: "", label: "Todas" }].concat(ROTULOS.map((r) => ({ value: r.codigo, label: r.rotulo })))}
              value={[{ value: "", label: "Todas" }].concat(ROTULOS.map((r) => ({ value: r.codigo, label: r.rotulo }))).find((o) => o.value === filtros.situacao)}
              onChange={(opt) => setFiltros((f) => ({ ...f, situacao: opt ? opt.value : "" }))}
              isSearchable={false}
            />
          </Col>
          <Col lg="3" md="6">
            <Label style={filterLabelStyle}>Período por</Label>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={DATA_CAMPOS}
              value={DATA_CAMPOS.find((o) => o.value === filtros.dataCampo)}
              onChange={(opt) => setFiltros((f) => ({ ...f, dataCampo: opt ? opt.value : "emissao" }))}
              isSearchable={false}
            />
          </Col>
          <Col lg="3" md="4">
            <Label style={filterLabelStyle}>De</Label>
            <Input type="date" value={filtros.dataDe} onChange={(e) => { const valor = e.target.value; setFiltros((f) => ({ ...f, dataDe: valor })); }} />
          </Col>
          <Col lg="3" md="4">
            <Label style={filterLabelStyle}>Até</Label>
            <Input type="date" value={filtros.dataAte} onChange={(e) => { const valor = e.target.value; setFiltros((f) => ({ ...f, dataAte: valor })); }} />
          </Col>
        </Row>
      </FilterBand>

      {/* PO1-3171: vencimento é outro eixo, não situação — fica junto dos filtros, como
          atalho com contagem (o select "Vencimento" fazia o mesmo sem mostrar o número).
          Os cards gravam direto em filtros + aplicados, então activeFiltersCount já conta. */}
      {resumo && (
        <MetricCardGroup
          eyebrow="Vencimento"
          title="Atalhos por vencimento"
          description="Clique para filtrar; clique de novo para desligar."
          gridStyle={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 260px))" }}
        >
          <MetricCard
            label="Vencidas"
            value={resumo.vencidas || 0}
            helper="Vencimento passado, ainda em aberto"
            iconClassName="tim-icons icon-calendar-60"
            tone="danger"
            active={aplicados.vencimento === "vencidas"}
            onClick={() => aplicar({ vencimento: aplicados.vencimento === "vencidas" ? "" : "vencidas" })}
          />
          <MetricCard
            label="A vencer"
            value={resumo.aVencer || 0}
            helper="Vencimento de hoje em diante"
            iconClassName="tim-icons icon-calendar-60"
            tone="warning"
            active={aplicados.vencimento === "a_vencer"}
            onClick={() => aplicar({ vencimento: aplicados.vencimento === "a_vencer" ? "" : "a_vencer" })}
          />
        </MetricCardGroup>
      )}

      <Row>
        <Col xl={selecionada ? 8 : 12} lg={selecionada ? 7 : 12}>
          <ShellCard eyebrow="Faturas" title="Lista" description={`${dados.total} fatura(s) no recorte.`}>
            {!empresa ? (
              <div style={stateBoxStyle}>Escolha um embarcador no topo da página (ou "Todos os embarcadores").</div>
            ) : loading && !dados.items.length ? (
              <div style={stateBoxStyle}>
                <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                <span>Carregando…</span>
              </div>
            ) : erro ? (
              <div style={stateBoxStyle} role="alert">Não foi possível carregar: {erro}</div>
            ) : dados.items.length === 0 ? (
              <div style={stateBoxStyle}>Nenhuma fatura neste recorte.</div>
            ) : (
              <>
                <TableSurface>
                  <Table size="sm" responsive hover className="hub-thead-accent">
                    <thead className="text-primary">
                      <tr>
                        <th><SortHeader label="Embarcador" field="embarcador" sort={sort} onSort={onSort} /></th>
                        <th>Estabelecimento</th>
                        <th><SortHeader label="Fatura" field="numero" sort={sort} onSort={onSort} /></th>
                        <th><SortHeader label="Emissão" field="emissao" sort={sort} onSort={onSort} /></th>
                        <th><SortHeader label="Vencimento" field="vencimento" sort={sort} onSort={onSort} /></th>
                        <th className="text-right"><SortHeader label="Valor" field="valor" sort={sort} onSort={onSort} align="right" /></th>
                        <th><SortHeader label="Liberação" field="liberacao" sort={sort} onSort={onSort} /></th>
                        <th><SortHeader label="Situação" field="situacao" sort={sort} onSort={onSort} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.items.map((f) => {
                        const alerta = getVencimentoAlert({ dataVencimentoExterna: f.dataVencimento, situacaoCodigo: f.situacaoTms }, SITUACOES_ALERTA_VENCIMENTO);
                        const ativa = selecionada && selecionada.id === f.id;
                        return (
                          <tr key={f.id} onClick={() => setSelecionada(ativa ? null : f)} style={{ cursor: "pointer", background: ativa ? AUTOPILOT_COLORS.insetSurface : undefined }}>
                            <td>{f.embarcador}</td>
                            <td>
                              <div>{f.estabelecimento}</div>
                              <div style={mutedStyle}>{f.estabelecimentoCnpj}</div>
                            </td>
                            <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{f.numero}</td>
                            <td>{fmtData(f.dataEmissao)}</td>
                            <td>
                              <div>{fmtData(f.dataVencimento)}</div>
                              {alerta && <div style={{ ...mutedStyle, color: `var(--everlog-status-${alerta.tone})` }}>{alerta.label}</div>}
                            </td>
                            <td className="text-right">{fmtBRL(f.valorFatura)}</td>
                            <td>{f.dataLiberacao ? fmtData(f.dataLiberacao) : "—"}</td>
                            <td>
                              <StatusBadge tone={toneDe(f.situacaoCodigo)}>{f.situacaoRotulo || "—"}</StatusBadge>
                              {f.situacaoCodigo === "reprovada" && (
                                <div style={mutedStyle}>{f.motivo || "Sem motivo informado"}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableSurface>
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={dados.total}
                  pageSizes={PAGE_SIZES}
                  itemLabel="fatura(s)"
                  onPageChange={setPage}
                  onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                  disabled={loading}
                />
              </>
            )}
          </ShellCard>
        </Col>
        {selecionada && (
          <Col xl="4" lg="5">
            <ShellCard eyebrow="Fatura em foco" title={`Fatura ${selecionada.numero}`} description={selecionada.embarcador}
              action={<Button size="sm" color="secondary" outline onClick={() => setSelecionada(null)}>Fechar</Button>}>
              <SectionCard variant="field">
                <LinhaResumo label="Situação" value={selecionada.situacaoRotulo} />
                {selecionada.situacaoCodigo === "reprovada" && <LinhaResumo label="Motivo" value={selecionada.motivo || "Sem motivo informado"} />}
                <LinhaResumo label="Estabelecimento" value={`${selecionada.estabelecimento || ""} ${selecionada.estabelecimentoCnpj ? `(${selecionada.estabelecimentoCnpj})` : ""}`} />
                <LinhaResumo label="Emissão" value={fmtData(selecionada.dataEmissao)} />
                <LinhaResumo label="Vencimento" value={fmtData(selecionada.dataVencimento)} />
                <LinhaResumo label="Valor da fatura" value={fmtBRL(selecionada.valorFatura)} />
                <LinhaResumo label="Liberada em" value={selecionada.dataLiberacao ? fmtData(selecionada.dataLiberacao) : "—"} />
              </SectionCard>
              {selecionada.situacaoCodigo === "reprovada" && (
                <AcoesReprovada
                  fatura={selecionada}
                  onConcluido={(mensagem) => {
                    addToast({ place: "tr", type: "success", title: `Fatura ${selecionada.numero}`, description: mensagem });
                    carregar();
                  }}
                />
              )}
              <p style={{ ...mutedStyle, marginTop: "0.75rem" }}>
                "Liberada" quer dizer que a fatura passou pela auditoria e seguiu para o financeiro do embarcador. Dúvidas sobre o pagamento são tratadas com o embarcador.
              </p>
            </ShellCard>
          </Col>
        )}
      </Row>
    </div>
  );
}
